import type { ConnectorItem, Endpoint, Item, Point, Rect, ShapeKind, Side } from './types';

export const rectOf = (it: { x: number; y: number; w: number; h: number }): Rect => ({ x: it.x, y: it.y, w: it.w, h: it.h });

export function union(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null;
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const r of rects) {
    x1 = Math.min(x1, r.x);
    y1 = Math.min(y1, r.y);
    x2 = Math.max(x2, r.x + r.w);
    y2 = Math.max(y2, r.y + r.h);
  }
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

export function intersects(a: Rect, b: Rect): boolean {
  return a.x <= b.x + b.w && b.x <= a.x + a.w && a.y <= b.y + b.h && b.y <= a.y + a.h;
}

export function containsRect(outer: Rect, inner: Rect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.w <= outer.x + outer.w && inner.y + inner.h <= outer.y + outer.h;
}

export function pointInRect(p: Point, r: Rect, pad = 0): boolean {
  return p.x >= r.x - pad && p.x <= r.x + r.w + pad && p.y >= r.y - pad && p.y <= r.y + r.h + pad;
}

export function normalizeRect(a: Point, b: Point): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

export const center = (r: Rect): Point => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });

export function inflate(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, w: r.w + by * 2, h: r.h + by * 2 };
}

export const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

export function distToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return dist(p, a);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function distToPolyline(p: Point, pts: Point[]): number {
  let d = Infinity;
  for (let i = 1; i < pts.length; i++) d = Math.min(d, distToSegment(p, pts[i - 1], pts[i]));
  return d;
}

// ---------------------------------------------------------------------------------------------
// Connectors

export const SIDES: Side[] = ['top', 'right', 'bottom', 'left'];

const NORMALS: Record<Side, Point> = {
  top: { x: 0, y: -1 },
  right: { x: 1, y: 0 },
  bottom: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
};

export function sidePoint(r: Rect, side: Side): Point {
  switch (side) {
    case 'top':
      return { x: r.x + r.w / 2, y: r.y };
    case 'right':
      return { x: r.x + r.w, y: r.y + r.h / 2 };
    case 'bottom':
      return { x: r.x + r.w / 2, y: r.y + r.h };
    case 'left':
      return { x: r.x, y: r.y + r.h / 2 };
  }
}

/** Side of `r` that faces point `p`. */
export function facingSide(r: Rect, p: Point): Side {
  const c = center(r);
  const dx = (p.x - c.x) / Math.max(r.w, 1);
  const dy = (p.y - c.y) / Math.max(r.h, 1);
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'bottom' : 'top';
}

export function nearestSide(r: Rect, p: Point): { side: Side; d: number } {
  let best: Side = 'top';
  let bd = Infinity;
  for (const s of SIDES) {
    const d = dist(p, sidePoint(r, s));
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return { side: best, d: bd };
}

export interface ResolvedEnd {
  p: Point;
  dir: Point | null;
  rect: Rect | null;
}

type Lookup = (id: string) => Item | undefined;

function attachedRect(end: Endpoint, get: Lookup): Rect | null {
  if (!end.id) return null;
  const it = get(end.id);
  if (!it || it.type === 'connector') return null;
  return rectOf(it);
}

/**
 * Where each end of a connector sits. Ends attached with side 'auto' face the other end, or
 * `via` when the line is pulled through a point.
 */
export function resolveEnds(c: Pick<ConnectorItem, 'from' | 'to'>, get: Lookup, bend?: Point): [ResolvedEnd, ResolvedEnd] {
  const ra = attachedRect(c.from, get);
  const rb = attachedRect(c.to, get);
  let ca = ra ? center(ra) : { x: c.from.x, y: c.from.y };
  let cb = rb ? center(rb) : { x: c.to.x, y: c.to.y };
  if (bend) {
    const via = { x: (ca.x + cb.x) / 2 + bend.x, y: (ca.y + cb.y) / 2 + bend.y };
    ca = cb = via;
  }
  const end = (e: Endpoint, r: Rect | null, other: Point): ResolvedEnd => {
    if (!r) return { p: { x: e.x, y: e.y }, dir: null, rect: null };
    const side = !e.side || e.side === 'auto' ? facingSide(r, other) : e.side;
    return { p: sidePoint(r, side), dir: NORMALS[side], rect: r };
  };
  return [end(c.from, ra, cb), end(c.to, rb, ca)];
}

export interface ConnectorGeometry {
  kind: 'poly' | 'bezier';
  /** Polyline points, or [p0, c1, c2, p3] for a bezier. */
  pts: Point[];
  startAngle: number;
  endAngle: number;
  mid: Point;
  bounds: Rect;
}

function inferDir(from: Point, to: Point): Point {
  const dx = to.x - from.x, dy = to.y - from.y;
  if (Math.abs(dx) >= Math.abs(dy)) return { x: Math.sign(dx) || 1, y: 0 };
  return { x: 0, y: Math.sign(dy) || 1 };
}

function simplify(pts: Point[]): Point[] {
  const out: Point[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 0.01 && Math.abs(last.y - p.y) < 0.01) continue;
    out.push(p);
  }
  for (let i = out.length - 2; i > 0; i--) {
    const a = out[i - 1], b = out[i], c = out[i + 1];
    const collinear = (Math.abs(a.x - b.x) < 0.01 && Math.abs(b.x - c.x) < 0.01) || (Math.abs(a.y - b.y) < 0.01 && Math.abs(b.y - c.y) < 0.01);
    if (collinear) out.splice(i, 1);
  }
  return out;
}

/** Orthogonal route; `bend` moves the middle segment (or, for an L, pulls the corner into a step). */
function elbow(p1: Point, d1: Point, p2: Point, d2: Point, bend: Point = { x: 0, y: 0 }): Point[] {
  const gap = 28;
  const a = { x: p1.x + d1.x * gap, y: p1.y + d1.y * gap };
  const b = { x: p2.x + d2.x * gap, y: p2.y + d2.y * gap };
  const h1 = d1.x !== 0, h2 = d2.x !== 0;
  let mid: Point[];
  if (h1 && h2) {
    const forward = d1.x * (b.x - a.x) >= 0 && d2.x * (a.x - b.x) >= 0;
    if (forward || d1.x === -d2.x) {
      const mx = (a.x + b.x) / 2 + bend.x, my = (a.y + b.y) / 2 + bend.y;
      mid = forward ? [{ x: mx, y: a.y }, { x: mx, y: b.y }] : [{ x: a.x, y: my }, { x: b.x, y: my }];
    } else {
      const x = (d1.x > 0 ? Math.max(a.x, b.x) : Math.min(a.x, b.x)) + bend.x;
      mid = [{ x, y: a.y }, { x, y: b.y }];
    }
  } else if (!h1 && !h2) {
    const forward = d1.y * (b.y - a.y) >= 0 && d2.y * (a.y - b.y) >= 0;
    if (forward || d1.y === -d2.y) {
      const my = (a.y + b.y) / 2 + bend.y, mx = (a.x + b.x) / 2 + bend.x;
      mid = forward ? [{ x: a.x, y: my }, { x: b.x, y: my }] : [{ x: mx, y: a.y }, { x: mx, y: b.y }];
    } else {
      const y = (d1.y > 0 ? Math.max(a.y, b.y) : Math.min(a.y, b.y)) + bend.y;
      mid = [{ x: a.x, y }, { x: b.x, y }];
    }
  } else if (h1) {
    // Out sideways, in from above or below: one corner, or a step when pulled.
    const cx = b.x + bend.x, cy = a.y + bend.y;
    mid = [{ x: cx, y: a.y }, { x: cx, y: cy }, { x: b.x, y: cy }];
  } else {
    const cx = a.x + bend.x, cy = b.y + bend.y;
    mid = [{ x: a.x, y: cy }, { x: cx, y: cy }, { x: cx, y: b.y }];
  }
  return simplify([p1, a, ...mid, b, p2]);
}

function polyLength(pts: Point[]): number {
  let l = 0;
  for (let i = 1; i < pts.length; i++) l += dist(pts[i - 1], pts[i]);
  return l;
}

function pointAlong(pts: Point[], t: number): Point {
  let target = polyLength(pts) * t;
  for (let i = 1; i < pts.length; i++) {
    const seg = dist(pts[i - 1], pts[i]);
    if (target <= seg && seg > 0) {
      const k = target / seg;
      return { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * k, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * k };
    }
    target -= seg;
  }
  return pts[pts.length - 1];
}

export function bezierPoint(p: Point[], t: number): Point {
  const u = 1 - t;
  return {
    x: u * u * u * p[0].x + 3 * u * u * t * p[1].x + 3 * u * t * t * p[2].x + t * t * t * p[3].x,
    y: u * u * u * p[0].y + 3 * u * u * t * p[1].y + 3 * u * t * t * p[2].y + t * t * t * p[3].y,
  };
}

export function sampleBezier(p: Point[], n = 24): Point[] {
  const out: Point[] = [];
  for (let i = 0; i <= n; i++) out.push(bezierPoint(p, i / n));
  return out;
}

export function connectorGeometry(c: ConnectorItem, get: Lookup): ConnectorGeometry {
  const bend = c.bend && (c.bend.x !== 0 || c.bend.y !== 0) ? c.bend : undefined;
  // An elbow line keeps facing ends; a pulled straight line or curve turns its ends toward the pull.
  const [a, b] = resolveEnds(c, get, c.route === 'elbow' ? undefined : bend);
  // The point a straight line or curve is pulled through.
  let via: Point | null = null;
  if (bend) {
    const o = pullOrigin(c, get);
    via = { x: o.x + bend.x, y: o.y + bend.y };
  }
  const d1 = a.dir ?? inferDir(a.p, b.p);
  const d2 = b.dir ?? inferDir(b.p, a.p);
  let pts: Point[];
  let kind: 'poly' | 'bezier' = 'poly';
  if (c.route === 'curve') {
    const k = Math.min(240, Math.max(36, dist(a.p, b.p) * 0.42));
    const c1 = { x: a.p.x + d1.x * k, y: a.p.y + d1.y * k };
    const c2 = { x: b.p.x + d2.x * k, y: b.p.y + d2.y * k };
    if (via) {
      // Moving both inner control points by 4/3 of an offset moves the curve's middle by that
      // offset: shift them so the middle lands exactly on the point it is pulled through.
      const m = bezierPoint([a.p, c1, c2, b.p], 0.5);
      const vx = ((via.x - m.x) * 4) / 3, vy = ((via.y - m.y) * 4) / 3;
      c1.x += vx;
      c1.y += vy;
      c2.x += vx;
      c2.y += vy;
    }
    pts = [a.p, c1, c2, b.p];
    kind = 'bezier';
  } else if (c.route === 'elbow') {
    pts = elbow(a.p, d1, b.p, d2, bend);
  } else if (via) {
    pts = [a.p, via, b.p];
  } else {
    pts = [a.p, b.p];
  }
  const sampled = kind === 'bezier' ? sampleBezier(pts) : pts;
  const s0 = sampled[0], s1 = sampled[1] ?? sampled[0];
  const e1 = sampled[sampled.length - 1], e0 = sampled[sampled.length - 2] ?? e1;
  const xs = sampled.map((p) => p.x), ys = sampled.map((p) => p.y);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  return {
    kind,
    pts,
    startAngle: Math.atan2(s0.y - s1.y, s0.x - s1.x),
    endAngle: Math.atan2(e1.y - e0.y, e1.x - e0.x),
    // A pulled straight line has its handle (and label) on the corner it was pulled by.
    mid: kind === 'bezier' ? bezierPoint(pts, 0.5) : c.route === 'straight' && bend ? pts[1] : pointAlong(pts, 0.5),
    bounds: { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY },
  };
}

/** A straight line or curve with `bend` passes through this point plus the bend: halfway between its ends' items. */
export function pullOrigin(c: Pick<ConnectorItem, 'from' | 'to'>, get: Lookup): Point {
  const ra = attachedRect(c.from, get), rb = attachedRect(c.to, get);
  const ca = ra ? center(ra) : { x: c.from.x, y: c.from.y };
  const cb = rb ? center(rb) : { x: c.to.x, y: c.to.y };
  return { x: (ca.x + cb.x) / 2, y: (ca.y + cb.y) / 2 };
}

export function connectorDistance(g: ConnectorGeometry, p: Point): number {
  return distToPolyline(p, g.kind === 'bezier' ? sampleBezier(g.pts) : g.pts);
}

// ---------------------------------------------------------------------------------------------
// Shapes

/** Polygon outline in local coordinates for polygonal shapes; null for curved ones. */
export function shapePolygon(kind: ShapeKind, w: number, h: number): number[] | null {
  switch (kind) {
    case 'triangle':
      return [w / 2, 0, w, h, 0, h];
    case 'diamond':
      return [w / 2, 0, w, h / 2, w / 2, h, 0, h / 2];
    case 'hexagon': {
      const k = Math.min(w * 0.25, h * 0.5);
      return [k, 0, w - k, 0, w, h / 2, w - k, h, k, h, 0, h / 2];
    }
    case 'parallelogram': {
      const k = Math.min(w * 0.22, h);
      return [k, 0, w, 0, w - k, h, 0, h];
    }
    case 'arrow': {
      const head = Math.min(w * 0.4, h);
      return [0, h * 0.25, w - head, h * 0.25, w - head, 0, w, h / 2, w - head, h, w - head, h * 0.75, 0, h * 0.75];
    }
    case 'star': {
      const pts: number[] = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? 0.5 : 0.21;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push(w / 2 + Math.cos(a) * r * w, h / 2 + Math.sin(a) * r * h * 1.05 + h * 0.03);
      }
      return pts;
    }
    case 'callout': {
      const body = h * 0.78;
      return [0, 0, w, 0, w, body, w * 0.42, body, w * 0.22, h, w * 0.26, body, 0, body];
    }
    default:
      return null;
  }
}

/** Area inside a shape where its label goes. */
export function textBox(kind: ShapeKind, w: number, h: number): Rect {
  switch (kind) {
    case 'ellipse':
      return { x: w * 0.15, y: h * 0.15, w: w * 0.7, h: h * 0.7 };
    case 'diamond':
      return { x: w * 0.25, y: h * 0.25, w: w * 0.5, h: h * 0.5 };
    case 'triangle':
      return { x: w * 0.25, y: h * 0.45, w: w * 0.5, h: h * 0.5 };
    case 'star':
      return { x: w * 0.3, y: h * 0.35, w: w * 0.4, h: h * 0.38 };
    case 'hexagon':
    case 'parallelogram':
      return { x: w * 0.2, y: h * 0.08, w: w * 0.6, h: h * 0.84 };
    case 'callout':
      return { x: w * 0.06, y: h * 0.06, w: w * 0.88, h: h * 0.66 };
    case 'cylinder':
      return { x: w * 0.08, y: h * 0.22, w: w * 0.84, h: h * 0.7 };
    case 'arrow':
      return { x: w * 0.06, y: h * 0.27, w: w * 0.6, h: h * 0.46 };
    default:
      return { x: w * 0.06, y: h * 0.06, w: w * 0.88, h: h * 0.88 };
  }
}
